import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-blue-100">
              Your privacy is important to us. This policy explains how we collect, 
              use, and protect your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Card className="border-none bg-white">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                Privacy Policy for Pathway Home Lending
              </CardTitle>
              <p className="text-gray-600">Last updated: January 2025</p>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <div className="space-y-8">
                
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h3>
                  <p className="text-gray-700 mb-4">
                    We collect information you provide directly to us, such as when you:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Apply for a mortgage loan</li>
                    <li>Use our calculators or online tools</li>
                    <li>Contact us for information or services</li>
                    <li>Subscribe to our newsletters or communications</li>
                    <li>Visit our website or use our services</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    This may include personal information such as your name, address, phone number, 
                    email address, Social Security number, employment information, financial information, 
                    and other information necessary to process your loan application.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h3>
                  <p className="text-gray-700 mb-4">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Process and service your loan application</li>
                    <li>Verify your identity and financial information</li>
                    <li>Communicate with you about our services</li>
                    <li>Comply with legal and regulatory requirements</li>
                    <li>Improve our services and customer experience</li>
                    <li>Protect against fraud and unauthorized transactions</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Information Sharing</h3>
                  <p className="text-gray-700 mb-4">
                    We may share your information with:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Lenders and investors in connection with your loan</li>
                    <li>Service providers who assist us in our business operations</li>
                    <li>Government agencies as required by law</li>
                    <li>Credit bureaus and other third parties for verification purposes</li>
                    <li>Legal and regulatory authorities when required</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    We do not sell your personal information to third parties for marketing purposes.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Data Security</h3>
                  <p className="text-gray-700">
                    We implement appropriate technical and organizational security measures to protect 
                    your personal information against unauthorized access, alteration, disclosure, or 
                    destruction. These measures include encryption, secure servers, and restricted access 
                    to personal information.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Your Rights</h3>
                  <p className="text-gray-700 mb-4">
                    You have the right to:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Access and review your personal information</li>
                    <li>Request corrections to inaccurate information</li>
                    <li>Opt out of certain communications</li>
                    <li>Request deletion of your information (subject to legal requirements)</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies and Tracking</h3>
                  <p className="text-gray-700">
                    Our website uses cookies and similar technologies to improve your browsing experience, 
                    analyze site traffic, and personalize content. You can control cookie settings through 
                    your browser preferences.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Third-Party Links</h3>
                  <p className="text-gray-700">
                    Our website may contain links to third-party websites. We are not responsible for 
                    the privacy practices of these external sites. We encourage you to review their 
                    privacy policies before providing any personal information.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h3>
                  <p className="text-gray-700">
                    Our services are not intended for individuals under the age of 18. We do not 
                    knowingly collect personal information from children under 18.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h3>
                  <p className="text-gray-700">
                    We may update this privacy policy from time to time. We will notify you of any 
                    material changes by posting the updated policy on our website with a new 
                    "Last updated" date.
                  </p>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h3>
                  <p className="text-gray-700 mb-4">
                    If you have questions about this privacy policy or our privacy practices, 
                    please contact us:
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-gray-700"><strong>Pathway Home Lending</strong></p>
                    <p className="text-gray-700">1499 W 120th Ave Suite 110</p>
                    <p className="text-gray-700">Westminster, CO 80234</p>
                    <p className="text-gray-700">Phone: (720) 650-7988</p>
                    <p className="text-gray-700">NMLS 2032871</p>
                  </div>
                </section>

                <section className="border-t pt-6">
                  <p className="text-sm text-gray-500">
                    This privacy policy is subject to applicable federal and state laws and regulations. 
                    For more information about our privacy practices related to mortgage lending, 
                    please refer to our Good Faith Estimate and other loan disclosures.
                  </p>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;